<script lang="ts" module>
	// asks hooks.server.ts to keep this page out of every cache, wherever editors put it, so a parent
	// coming back reaches the server and their saved answers rather than a cached blank form
	export const load: Load = async ({ event }) => event.setHeaders({ 'x-no-store': '1' })
</script>

<script lang="ts">
	import Field from '#components/field.svelte'
	import Radio from '#components/radio.svelte'
	import Checkbox from '#components/checkbox.svelte'
	import Button from '#components/button.svelte'
	import Shape from '#components/shape.svelte'
	import Morph from '#components/morph.svelte'
	import Captcha from 'postboi/svelte'
	import { enhance } from '$app/forms'
	import { onMount, tick } from 'svelte'
	import { fade, fly, slide } from 'svelte/transition'
	import { cubicInOut, cubicOut } from 'svelte/easing'
	import { scrollto } from '#library/scrollto.ts'
	import { track } from '#library/track.ts'
	import { year_groups, hear_options, max_children, collect, type Answers } from '#library/prospectus.ts'
	import { browser } from '$app/env'
	import { page } from '$app/state'
	import type { Storyblok } from '#library/storyblok.svelte.ts'
	import type { Prospectus } from '#library/types.ts'
	import type { Asset } from 'storyloco/asset'

	interface Props {
		data: Storyblok<Prospectus>
	}

	let { data }: Props = $props()

	const content = $derived(data.story?.content)
	const titles = ['Who’s this prospectus for?', 'Your preferred path at Bedales', 'Parent / Guardian Details']

	let step = $state(0)
	// where the form is going: it changes on the click, step only once the old step has slid away,
	// so whatever answers it (the back button) moves straight away
	let heading = $state(0)
	let form: HTMLFormElement | undefined = $state()

	/*
	 * A parent who leaves part way finds their answers waiting when they come back, already in the
	 * form as the page arrives. They're kept in a cookie as they're typed, scoped to this page and
	 * cleared once the request has gone. The server renders from it on a full load (hooks.server.ts
	 * keeps those pages out of every cache); arriving from another page, the browser reads it before
	 * the form first draws.
	 */
	const cookie = 'prospectus'

	function remembered(): Partial<Answers> {
		const raw = browser
			? document.cookie
					.split('; ')
					.find(pair => pair.startsWith(`${cookie}=`))
					?.slice(cookie.length + 1)
			: page.data.prospectus
		try {
			return raw ? JSON.parse(browser ? decodeURIComponent(raw) : raw) : {}
		} catch {
			return {}
		}
	}

	const answers = remembered()
	let children = $state(
		(answers.children?.length ? answers.children : [undefined])
			.slice(0, max_children)
			.map((child, id) => ({ id, ...child }))
	)

	// their prospectus, once the request has gone (the server seals who it's for into the link)
	let destination: string | undefined = $state()

	function remember(value: string, age: number) {
		document.cookie = `${cookie}=${value}; path=${location.pathname}; max-age=${age}; samesite=lax${location.protocol === 'https:' ? '; secure' : ''}`
	}

	function save() {
		if (!form) return
		const value = encodeURIComponent(JSON.stringify(collect(new FormData(form))))
		// a cookie holds about 4KB; answers that long just aren't remembered
		if (value.length < 3800) remember(value, 60 * 60 * 24 * 30)
	}

	let status = $state<'idle' | 'submitting' | 'success' | 'error'>('idle')
	let fieldsets: Array<HTMLFieldSetElement> = $state([])

	// the bar fills a step at a time, the last quarter once the request has gone
	// it starts empty and grows to the first step once the page is up
	let shown = $state(false)
	onMount(() => void setTimeout(() => (shown = true), 100))
	const progress = $derived(shown ? (status === 'success' ? 1 : (step + 1) / (titles.length + 1)) : 0)
	const aside = $derived(content?.steps?.[step])

	/** An asset field can hold one image or several; these all take one. */
	const one = (asset?: Asset | Array<Asset>) => (Array.isArray(asset) ? asset[0] : asset)
	const aside_image = $derived(one(aside?.image))

	/**
	 * Open the space (moving what's beside it over) while the button fades and scales in, whole rather
	 * than wiped in. Leaving runs it backwards.
	 */
	function open(node: HTMLElement) {
		const width = node.offsetWidth
		const margin = parseFloat(getComputedStyle(node).marginInlineEnd)
		return {
			duration: 320,
			css: (t: number) => {
				const room = cubicOut(t)
				const shown = cubicOut(t)
				return `width: ${room * width}px; margin-inline-end: ${room * margin}px; opacity: ${shown}; scale: ${0.8 + 0.2 * shown};`
			}
		}
	}

	/** Report the first field on this step that isn't filled in properly, if there is one. */
	function valid(index: number) {
		const fields = fieldsets[index]?.querySelectorAll<HTMLInputElement>('input, select, textarea') ?? []
		return [...fields].every(field => field.reportValidity())
	}

	/** Once a new child has slid open, bring it up under the header with the cursor in its name. */
	function added({ currentTarget }: Event) {
		const child = currentTarget as HTMLElement
		child.querySelector('input')?.focus({ preventScroll: true })
		child.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}

	let title: HTMLElement | undefined = $state()
	let moving = false
	let running: Array<Animation | undefined> = []
	let queued: number | undefined

	/** Slide this step's title and fields up and away, and the next step's up in (down, going back). */
	async function go(index: number) {
		index = Math.max(0, Math.min(index, titles.length - 1))
		// a click mid-move isn't dropped: the move jumps to its end and this one goes next
		if (moving) {
			if (index === heading) return
			queued = heading = index
			running.forEach(animation => animation?.finish())
			return
		}
		if (index === step) return
		if (index > step && !valid(step)) {
			heading = step
			return
		}
		const away = index > step ? -1 : 1
		const still = matchMedia('(prefers-reduced-motion: reduce)').matches

		/** Move the title and fields together, the fields `stagger` ms behind the title. */
		const slide = (from: number, to: number, duration: number, easing: string, stagger = 0) =>
			[title, fieldsets[step]].map((element, i) =>
				element?.animate(
					[
						{ translate: `0 ${from}px`, opacity: from ? 0 : 1 },
						{ translate: `0 ${to}px`, opacity: to ? 0 : 1 }
					],
					// hold the start before (a staggered element waits hidden) and the end after (so a
					// finished exit doesn't flash back before the arrival takes over)
					{ duration: still ? 0 : duration, delay: still ? 0 : i * stagger, easing, fill: 'both' }
				)
			)
		const done = (animations: Array<Animation | undefined>) =>
			Promise.all(animations.map(animation => animation?.finished)).catch(() => {})

		moving = true
		heading = index
		try {
			// a short lift away, then a longer, softer settle in from the other side
			const exit = (running = slide(0, 12 * away, 240, 'cubic-bezier(0.4, 0, 1, 1)'))
			await done(exit)
			step = index
			// jump to the top while nothing's showing, rather than scrolling during the move
			window.scrollTo({ top: 0, behavior: 'instant' })
			await tick()
			const arrival = (running = slide(-24 * away, 0, 600, 'cubic-bezier(0.22, 1, 0.36, 1)', 70))
			exit.forEach(animation => animation?.cancel())
			// already asked to move on: don't make them sit through this arrival
			if (queued !== undefined) arrival.forEach(animation => animation?.finish())
			await done(arrival)
			arrival.forEach(animation => animation?.cancel())
		} finally {
			moving = false
			running = []
		}

		if (queued !== undefined) {
			const next = queued
			queued = undefined
			go(next)
		}
	}
</script>

{#snippet figure(image: Asset | Array<Asset> | undefined, shape: string | number | undefined, width: number)}
	{@const asset = one(image)}
	{#if asset?.filename}
		<div class="figure" style:--width="{width}px">
			<Shape name={String(shape ?? '') as never} src={asset.filename} alt={asset.alt} focus={asset.focus} />
		</div>
	{/if}
{/snippet}

<form
	bind:this={form}
	oninput={save}
	onchange={save}
	class="prospectus theme-crocus-on-gimson"
	method="post"
	action="/?/prospectus"
	novalidate
	style:--progress={progress}
	use:enhance={({ cancel }) => {
		if (!valid(step)) return cancel()
		status = 'submitting'

		return async ({ result }) => {
			status = result.type === 'success' ? 'success' : 'error'
			if (status === 'success') {
				track('Prospectus Requested', { children: children.length })
				remember('', 0)
				const link = result.type === 'success' ? (result.data?.link as string | undefined) : undefined
				destination = link
				if (link) setTimeout(() => location.assign(link), 3500)
			}
			scrollto(0)
		}
	}}
>
	<input type="hidden" name="_story" value={data.story?.id ?? ''} />
	<div class="block contain">
		{#if status === 'success'}
			<div class="done" in:fade>
				{@render figure(content?.success_image, content?.success_shape, 360)}
				<h1 class="title">{content?.success_title || 'Thank you'}</h1>
				<p class="text">
					{content?.success_text ||
						'Your prospectus request is with our admissions team. Look out for an email from us soon.'}
				</p>
				{#if destination}
					<a class="now" href={destination}>Take me there now</a>
				{/if}
			</div>
		{:else}
			<h1 class="title" bind:this={title}>{titles[step]}</h1>
			{#if status === 'error'}
				<span class="error" transition:slide>Something went wrong, please try again.</span>
			{/if}

			<div class="row">
				<div class="column">
					<fieldset class="set" hidden={step !== 0} bind:this={fieldsets[0]}>
						<div class="children">
							{#each children as child, i (child.id)}
								<div class="child" transition:slide onintroend={added}>
									{#if i}
										<div class="divider">Child {i + 1}</div>
									{/if}
									<Field
										id="child_first_name_{child.id}"
										value={child.first_name}
										name="child_first_name"
										label="Your child’s first name"
										placeholder="Enter child’s first name"
										required
									/>
									<div class="pair">
										<Field
											type="select"
											id="child_current_{child.id}"
											value={child.current}
											name="child_current"
											label="Current year group"
											placeholder="Select year group"
											options={[...year_groups]}
											required
										/>
										<Field
											type="select"
											id="child_entry_{child.id}"
											value={child.entry}
											name="child_entry"
											label="Proposed year group of entry"
											placeholder="Select year group"
											options={[...year_groups]}
											required
										/>
									</div>
									<Field
										id="child_school_{child.id}"
										value={child.school}
										name="child_school"
										label="Current school"
										placeholder="Enter school name"
										optional
									/>
								</div>
							{/each}
						</div>
						<div class="add">
							{#if children.length < max_children}
								<Button
									class="add-child theme-pink-on-lupton"
									icon="plus"
									onclick={() => children.push({ id: Date.now() })}>Add another child</Button
								>
							{/if}
							{#if children.length > 1}
								<Button
									class="remove-child"
									outline
									icon="close"
									label="Remove child {children.length}"
									onclick={() => children.pop()}
								/>
							{/if}
						</div>
					</fieldset>

					<fieldset class="set" hidden={step !== 1} bind:this={fieldsets[1]}>
						<Radio
							deselectable
							optional
							value={answers.boarding}
							name="boarding"
							label="Day or Boarding"
							options={[
								{ id: 'boarding_day', value: 'day', label: 'Day' },
								{ id: 'boarding_boarding', value: 'boarding', label: 'Boarding' },
								{ id: 'boarding_unsure', value: 'unsure', label: 'Not sure' }
							]}
						/>
						<Radio
							value={answers.location}
							name="location"
							label="Where are you situated?"
							required
							options={[
								{ id: 'location_uk', value: 'uk', label: 'UK' },
								{ id: 'location_international', value: 'international', label: 'International' }
							]}
						/>
					</fieldset>

					<fieldset class="set" hidden={step !== 2} bind:this={fieldsets[2]}>
						<div class="pair">
							<Field
								id="first_name"
								value={answers.first_name}
								name="first_name"
								label="First name"
								required
							/>
							<Field id="last_name" value={answers.last_name} name="last_name" label="Surname" required />
						</div>
						<Field
							type="email"
							id="email"
							value={answers.email}
							name="email"
							label="Email address"
							placeholder="We’ll send a brochure link here"
							required
						/>
						<Field
							type="tel"
							id="phone"
							value={answers.phone}
							name="phone"
							label="Contact number"
							placeholder="For example 07700 900000"
							required
						/>
						<Field
							type="select"
							id="hear"
							value={answers.hear}
							name="hear"
							label="Where did you hear about Bedales?"
							placeholder="Select option"
							options={hear_options.map(option => ({
								value: option,
								label: option.trim(),
								disabled: false,
								selected: false
							}))}
							required
						/>
						<p class="caption">*All fields required</p>
						<Checkbox
							value={answers.consent ? ['yes'] : []}
							name="consent"
							options={[
								{
									id: 'consent',
									value: 'yes',
									label: 'I’d like to hear from Bedales about open days, events and news'
								}
							]}
						/>
						<Captcha />
					</fieldset>
				</div>

				<aside class="aside">
					<div class="feature">
						{#if aside_image?.filename}
							<!-- one picture for the whole form: its shape morphs into the next step's -->
							<div class="figure" style:--width="327px">
								<Morph
									name={String(aside?.shape ?? '')}
									src={aside_image.filename}
									alt={aside_image.alt}
									focus={aside_image.focus}
								/>
							</div>
						{/if}
						<!-- both quotes share one clipped spot: the old one slides up out of it as the new one slides up in -->
						<div class="quotes">
							{#key step}
								<p
									class="quote"
									in:fly={{ y: '100%', opacity: 1, duration: 600, easing: cubicInOut }}
									out:fly={{ y: '-100%', opacity: 1, duration: 600, easing: cubicInOut }}
								>
									{aside?.quote ?? ''}
								</p>
							{/key}
						</div>
					</div>
				</aside>
			</div>
		{/if}
	</div>

	{#if status !== 'success'}
		<div class="foot">
			<div class="buttons contain">
				{#if heading > 0}
					<span class="back" transition:open>
						<Button onclick={() => go(heading - 1)} outline icon="arrow-left" label="Previous step" />
					</span>
				{/if}
				{#if step < titles.length - 1}
					<Button onclick={() => go(heading + 1)} icon="arrow-right">Next</Button>
				{:else}
					<Button type="submit" icon="check" loading={status === 'submitting'}
						>Generate your prospectus</Button
					>
				{/if}
			</div>
		</div>
	{/if}

	<div class="progress" role="progressbar" aria-label="Progress" aria-valuenow={Math.round(progress * 100)}></div>
</form>

<style lang="css">
	/* the page is light gimson everywhere else; this one is the design's gimson, right up under
	   the header */
	:global(main.main:has(> .prospectus)) {
		background-color: var(--c-gimson);
	}

	.prospectus {
		display: flex;
		flex-direction: column;
		/* fill the screen below the fixed header (main's top padding) */
		min-height: calc(100svh - 3.75rem);

		@media (--from-mobile) {
			min-height: calc(100svh - 6.9375rem);
		}
	}

	.block {
		flex: 1;
		padding-block: 2.5rem 6.25rem;
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h3);
		letter-spacing: var(--ls-h3);
		margin-block-end: 3rem;
	}

	.error {
		color: var(--c-error, crimson);
		display: block;
		padding-block-end: 1.5rem;
	}

	/* the form, and beside it on wider screens the step's picture and quote */
	.row {
		display: grid;
		gap: 2rem;

		@media (--from-tablet) {
			grid-template-columns: minmax(0, 38.75rem) minmax(0, 25rem);
			justify-content: space-between;
		}
	}

	.aside {
		display: none;

		@media (--from-tablet) {
			display: block;
		}
	}

	.feature {
		align-items: center;
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
	}

	.figure {
		width: min(var(--width), 100%);
	}

	.quotes {
		display: grid;
		overflow: clip;
	}

	.quote {
		color: var(--c-foreground);
		grid-area: 1 / 1;
		/* no paragraph margin inside the clip, or an outgoing quote stops short of leaving it */
		margin: 0;
		text-wrap: balance;
		font: 500 var(--f-h6);
		letter-spacing: var(--ls-h6);
		text-align: center;
	}

	.set {
		border: 0;
		display: flex;
		flex-direction: column;
		gap: 2rem;
		padding: 0;

		&[hidden] {
			display: none;
		}
	}

	/* no gap here: spacing between children is each one's own padding, which slide animates, so a
	   child opening or closing doesn't jump by the gap at the end */
	.children {
		display: flex;
		flex-direction: column;
	}

	.child {
		display: flex;
		flex-direction: column;
		gap: 2rem;
		/* clear the sticky header when scrolled to */
		scroll-margin-block-start: 6rem;

		& + & {
			padding-block-start: 2rem;
		}
	}

	.divider {
		border-block-start: 1px solid color-mix(in srgb, var(--c-lupton) 20%, transparent);
		color: var(--c-foreground);
		font: 500 1rem / 1.4 var(--f-family);
		letter-spacing: var(--ls-body2);
		padding-block-start: 1rem;
	}

	.pair {
		display: grid;
		gap: 2rem;

		@media (--from-mobile) {
			grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		}
	}

	.caption {
		color: var(--c-foreground);
		font: 400 var(--f-signpost);
		letter-spacing: var(--ls-signpost);
		margin-block-start: -1rem;
	}

	/* the design's fields and choices: a quiet crocus edge all the time, drawn in fully on focus or
	   when chosen (the inset edge sits under the outline the components already animate) */
	.prospectus :global(.field) {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
	}

	/* labels, and the buttons, are the design's Body2 Medium, 16/140, a step up from the site's
	   smaller body2 */
	.prospectus :global(.button) {
		font: 500 1rem / 1.4 var(--f-family);
	}

	.prospectus :global(.field .label),
	.prospectus :global(.radios .label) {
		font: 500 1rem / 1.4 var(--f-family);
		margin-block-end: 0.625rem;
	}

	.prospectus :global(.field .inner),
	.prospectus :global(.radio) {
		box-shadow: inset 0 0 0 1.5px color-mix(in srgb, var(--c-crocus) 40%, transparent);
	}

	/* a field's focus outline sits right over its quiet edge, so focusing just deepens it */
	.prospectus :global(.field .inner),
	.prospectus :global(.input:checked + .radio) {
		outline-offset: -1.5px;
	}

	.prospectus :global(.radios .inner) {
		gap: 1rem;
	}

	.prospectus :global(.radio) {
		border-radius: 0.25rem;
		color: var(--c-charcoal);
	}

	/* the design's asterisk sits tight against the label, in its colour */
	.prospectus :global([data-required]:not([data-required='false'])::after) {
		color: inherit;
		margin-inline-start: 0;
	}

	.add {
		display: flex;
		gap: 0.75rem;
	}

	/* a gentler hover than the site's outline: the pink lifts a little */
	.add :global(.add-child) {
		transition:
			background-color 0.2s ease,
			outline-color 0.2s ease,
			outline-offset 0.2s ease,
			scale 0.1s ease;
	}

	/* its theme's pink would barely show against the page */
	.add :global(.add-child:focus-visible) {
		outline-color: var(--c-lupton);
	}

	.add :global(.add-child::after) {
		display: none;
	}

	.add :global(.add-child:hover),
	.add :global(.add-child:active) {
		background-color: color-mix(in srgb, var(--c-pink), var(--c-white) 35%);
		color: var(--c-lupton);
	}

	.add :global(.add-child:active) {
		scale: 0.97;
	}

	/* the ×: its outline stays put, and a faint wash of its colour comes in instead */
	.add :global(.remove-child) {
		transition:
			background-color 0.2s ease,
			outline-color 0.2s ease,
			outline-offset 0.2s ease,
			scale 0.1s ease;
	}

	.add :global(.remove-child:hover::after) {
		border-width: 1.5px;
	}

	.add :global(.remove-child:hover),
	.add :global(.remove-child:active) {
		background-color: color-mix(in srgb, var(--c-foreground) 8%, transparent);
		color: var(--c-foreground);
	}

	.add :global(.remove-child:active) {
		scale: 0.97;
	}

	.done {
		align-items: center;
		display: flex;
		flex-direction: column;
		gap: 1rem;
		margin-inline: auto;
		max-width: 41.25rem;
		padding-block-start: 2rem;
		text-align: center;

		.title {
			margin-block: 1rem 0;
			text-wrap: balance;
		}
	}

	.now {
		color: var(--c-foreground);
		font: 500 1rem / 1.4 var(--f-family);
		text-decoration: underline;
		text-underline-offset: 0.2em;
	}

	.text {
		color: var(--c-foreground);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		text-wrap: balance;
	}

	/* the step's buttons, on a light band that stays just above the progress bar */
	.foot {
		background-color: var(--c-gimson-light);
		bottom: 1.5rem;
		position: sticky;
		z-index: 1;
	}

	/* no gap: the back button's space is its own margin, which opens and closes with it */
	.buttons {
		display: flex;
		padding-block: 1.5rem;
	}

	.back {
		display: flex;
		flex-shrink: 0;
		margin-inline-end: 1rem;
		transform-origin: left center;
	}

	/* the pattern fills the bar a step at a time; the bar keeps to the bottom edge of the screen.
	   Sticky, not fixed: the page scales in as it loads, and a fixed bar would sit against the
	   scaling page (off the bottom of the screen) until that finished */
	.progress {
		background-color: var(--c-white);
		bottom: 0;
		flex-shrink: 0;
		height: 1.5rem;
		position: sticky;
		z-index: 1;

		/* the pattern spans the whole bar and stays put; a clip uncovers it, so it never rescales */
		&::before {
			background-image: var(--pattern);
			clip-path: inset(0 calc((1 - var(--progress)) * 100%) 0 0);
			content: '';
			inset: 0;
			position: absolute;
			transition: clip-path 0.6s cubic-bezier(0.65, 0, 0.35, 1);
		}
	}
</style>
